<template>
	<div class="field-settings date-time" v-if="localField">
		<v-expansion-panels v-model="panel" flat accordion>
			<ExpansionPanel :title="'Basics'">
				<SettingSwitch
					:title="'Label'"
					:placeholder="'Type your title here'"
					:switchValue.sync="titleEnabled"
					:textFieldValue.sync="titleValue"
				/>
				<SettingSwitch :title="'Required'" :switchValue.sync="requiredEnabled" />
				<SettingSwitch :title="'Prefill'" :switchValue.sync="prefillEnabled" />
				<div class="align-center mb-4 prefill-row display-flex flex-row width-100" v-if="prefillEnabled">
					<div class="width-100">
						<div class="pt-2">Date:</div>
						<CombinedInputFieldDataMenu
							:textAreaMode="false"
							:value.sync="prefillDateValue"
							:attachTo="'.prefill-row'"
							:valuesTypes="dataInputMenuValueTypes"
						/>
						<div class="sub-text">Accepted format: YYYY-MM-dd</div>

						<div class="pt-2">Time:</div>
						<CombinedInputFieldDataMenu
							:textAreaMode="false"
							:value.sync="prefillTimeValue"
							:attachTo="'.prefill-row'"
							:valuesTypes="dataInputMenuValueTypes"
						/>
						<div class="sub-text">Accepted format: HH:mm</div>
					</div>
				</div>
			</ExpansionPanel>
			<ExpansionPanel :title="'Options'">
				<div class="pt-2">Timezone:</div>
				<div class="pb-2">
					<AppSelect
						:items="timezoneSelectorOptions"
						outlined
						dense
						placeholder="Select timezone option"
						v-model="timezoneOptionSelected"
						item-text="title"
						item-value="value"
					></AppSelect>

					<div v-if="setCustomTimezone" class="pt-2">
						<div>
							<SelectTimezone class="mt-2" :userTimezone.sync="customTimezoneValue" />
							<div class="px-2 display-flex flex-column sub-text">
								<div>If you and your team are not in the same timezones, we recommend you stay on UTC.</div>
								<div>If not, suggested timezone is {{ Intl.DateTimeFormat().resolvedOptions().timeZone }}.</div>
							</div>
						</div>
					</div>
				</div>
				<SettingSwitch :title="'Show timezone'" :switchValue.sync="showTimezoneEnabled" />

				<div class="pt-2">Time:</div>
				<SettingSwitch :title="'Show 24 hours format'" :switchValue.sync="timeFormatAllHoursEnabled" />
			</ExpansionPanel>
			<ExpansionPanel :title="'Explanations'">
				<SettingSwitch
					:title="'Description'"
					:placeholder="'Type your description here'"
					:switchValue.sync="descriptionEnabled"
					:textFieldValue.sync="descriptionValue"
				/>
			</ExpansionPanel>
			<ExpansionPanel :title="'Validation'">
				<div>
					<div no-gutters class="align-center" v-for="option in validationItems" :key="option.key">
						<div v-if="option.optional">
							<AppCheckbox
								dense
								color="primary"
								:label="option.name"
								:isOn="localField.metadata.validations[option.key].enabled"
								@update:isOn="(v) => updateValidationEnabled(option.key, v)"
							/>
							<div class="px-2 pb-2" v-if="localField.metadata.validations[option.key].enabled">
								<SettingSwitch
									:title="'Map data'"
									:switchValue="localField.metadata.validations[option.key].is_mapped"
									@update:switchValue="(v) => setLocalFieldKey(`metadata.validations.${option.key}.is_mapped`, v)"
								/>
								<div v-if="!localField.metadata.validations[option.key].is_mapped">
									<AppDatePickerMenu
										v-if="option.isDate"
										:value="localField.metadata.validations[option.key].value"
										@input="(v) => updateValidationValue(option.key, v)"
									/>
									<AppTimePickerMenu
										v-if="option.isTime"
										:value="localField.metadata.validations[option.key].value"
										@input="(v) => updateValidationValue(option.key, v)"
										format="24hr"
									/>
								</div>
								<div v-else class="mb-2">
									<CombinedInputFieldDataMenu
										:value="localField.metadata.validations[option.key].value"
										@update:value="(v) => updateValidationValue(option.key, v)"
										:valuesTypes="dataInputMenuValueTypes"
									/>
									<div class="sub-text">Accepted format: {{ option.isDate ? 'YYYY-MM-dd' : 'HH:mm' }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div>
					<AppCheckbox
						dense
						color="primary"
						label="Time interval in minutes"
						:isOn="localField.metadata.validations.time_interval.enabled"
						@update:isOn="(v) => updateValidationEnabled('time_interval', v)"
					/>
					<div v-if="localField.metadata.validations.time_interval.enabled" class="mb-2">
						<CombinedInputFieldDataMenu
							:value="localField.metadata.validations.time_interval.value"
							@update:value="(v) => updateValidationValue('time_interval', v)"
							:valuesTypes="dataInputMenuValueTypes"
						/>
					</div>
				</div>
			</ExpansionPanel>
		</v-expansion-panels>
	</div>
</template>

<script>
import moment from 'moment-timezone';
import SettingSwitch from './WidgetSettings/SettingSwitch';
import SelectTimezone from '@/components/SelectTimezone.vue';
import { DATE_TIME_TIMEZONE_OPTIONS } from '@/utils/widget-service.const';
import CombinedInputFieldDataMenu from '@/components/DataInput/CombinedInputFieldDataMenu';
import ItemOnlyAllowedValueTypesMixin from './ValueTypesMixins/ItemOnlyAllowedValueTypesMixin';
import FieldSettingsMixin from '@/components/BlockEditorWidget/FieldSettings/FieldSettingsMixin';
export default {
	name: 'DateTimePickerFieldSettings',
	components: { SettingSwitch, CombinedInputFieldDataMenu, SelectTimezone },
	mixins: [FieldSettingsMixin, ItemOnlyAllowedValueTypesMixin],
	computed: {
		timezonesOptions() {
			return moment.tz.names();
		},
		setCustomTimezone() {
			return this.localField.metadata.options.timezone?.option_selected?.value === DATE_TIME_TIMEZONE_OPTIONS.setCustom;
		},
		showTimezoneEnabled: {
			get() {
				return this.localField.metadata.options.timezone?.show?.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.timezone.show.enabled', value);
			},
		},
		timeFormatAllHoursEnabled: {
			get() {
				return this.localField.metadata.options.time?.all_hours_format?.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.time.all_hours_format.enabled', value);
			},
		},
		customTimezoneValue: {
			get() {
				return this.localField.metadata.options.timezone?.custom?.value;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.timezone.custom.value', value);
			},
		},
		timezoneOptionSelected: {
			get() {
				return this.localField.metadata.options.timezone?.option_selected?.value;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.timezone.option_selected.value', value);
			},
		},
		prefillDateValue: {
			get() {
				return this.prefillValue?.date;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.prefill.value.date', value);
			},
		},
		prefillTimeValue: {
			get() {
				return this.prefillValue?.time;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.prefill.value.time', value);
			},
		},
	},
	data() {
		return {
			menu: false,
			localVal: null,
			validationItems: [
				{
					key: 'min_date',
					name: 'Earliest date',
					optional: true,
					isDate: true,
				},
				{
					key: 'max_date',
					name: 'Latest Date',
					optional: true,
					isDate: true,
				},
				{
					key: 'min_time',
					name: 'Earliest time',
					optional: true,
					isTime: true,
				},
				{
					key: 'max_time',
					name: 'Latest time',
					optional: true,
					isTime: true,
				},
			],
			timezoneSelectorOptions: [
				{
					title: 'Automatically detect timezone',
					value: 0,
				},
				{
					title: 'Let user choose timezone',
					value: 1,
				},
				{
					title: 'Set custom timezone',
					value: 2,
				},
			],
		};
	},
};
</script>

<style scoped lang="scss">
.date-time {
	.sub-text {
		font-size: 12px;
		opacity: 0.5;
	}
}
</style>
